<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import ThreeStateSwitch from './ThreeStateSwitch.vue'
import { useBrandIcon } from '@/composables/useBrandIcon'
import { useStore } from 'vuex';
import { createShareService } from '@/services/share.service';
import { Star } from 'lucide-vue-next'

const props = defineProps<{
  bookie: { id: string; name: string; icon: string }
  value: 0 | 1 | 2
}>()

const emit = defineEmits<{
  (e: 'change', newValue: 0 | 1 | 2): void
}>()

const store = useStore()
const shareService = createShareService(store)
const internalValue = ref(props.value)
const iconSrc = ref<string | null>(null)

watch(() => props.value, val => { internalValue.value = val })

async function loadIcon() {
  try {
    const logoData = shareService.getLogoData({brand: props.bookie.icon})
    iconSrc.value = await useBrandIcon({
      context: props.bookie.icon,
      background: logoData.background,
      width: 64,
      height: 64,
      cornerRadius: 0
    })
  } catch (err) {
    console.error('❌ Failed to load brand icon:', err)
    iconSrc.value = null
  }
}

onMounted(loadIcon)

function emitChange(newValue: 0 | 1 | 2) {
  internalValue.value = newValue
  emit('change', newValue)
}
</script>

<template>
  <div 
    class="flex items-center justify-between px-4 py-3 transition-all hover:bg-gray-50/80 dark:hover:bg-white/5 group border-b border-gray-100/50 dark:border-white/5 last:border-0"
  >
    <div class="flex items-center gap-3 min-w-0">
      <div class="relative flex-shrink-0">
        <div class="w-8 h-8 rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 bg-white dark:bg-gray-800 shadow-sm transition-transform group-hover:scale-110">
          <img 
            v-if="iconSrc"
            :src="iconSrc"
            :alt="bookie.name"
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800 text-[10px] font-bold text-gray-400">
            {{ bookie.name.substring(0, 2) }}
          </div>
        </div>
        <div v-if="internalValue === 2" class="absolute -top-0.5 -right-0.5 p-px bg-yellow-400 rounded-full shadow-sm border border-white dark:border-gray-900">
          <Star class="w-[6px] h-[6px] text-white fill-current" />
        </div>
      </div>
      
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-bold text-gray-700 dark:text-gray-200 truncate group-hover:text-primary transition-colors">
          {{ bookie.name }}
        </span>
        <span class="text-[10px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-tight">
          {{ internalValue === 0 ? 'Excluded' : internalValue === 1 ? 'Auto' : 'Preferred' }}
        </span>
      </div>
    </div>

    <div class="flex-shrink-0 w-32 ml-4">
      <ThreeStateSwitch
        :value="internalValue"
        @change="emitChange"
      />
    </div>
  </div>
</template>
